import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { createInstrumentProvider, ConnectionState } from '@/instruments/instrumentProvider';
import { createStabilityGate } from '@/instruments/stabilityGate';
import { STATUS, UNIT } from '@/instruments/dymoParser';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { FlaskConical, Save, Square, Zap, Usb, Hand, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { toastApiError } from '@/lib/errors';

// Fullscreen měřicí obrazovka — dedikovaná routa `/calibrations/:id/run`.
// Refaktor: pull simulace (setInterval + readValue) → PUSH reálná komunikace (WebHID/DYMO M5)
// s vodicí sekvencí a auto-záchytem stabilní hodnoty (viz instruments/*). Ruční zadání je
// vždy dostupný fallback. Metrologicky správný výpočet (per-směr, konvenční hmotnost) řeší
// BACKEND při výpočtu nejistoty / DCC; FE average je jen indikativní.

const PHASE = { ASC: 'ascending', DESC: 'descending' };

// Sdílený přepočet bodu (indikativní pro UI; backend přepočítává autoritativně per-směr).
function recomputePoint(pt) {
  const all = pt.series.flatMap((s) => [s.ascending, s.descending]).filter((v) => v);
  if (all.length > 0) {
    pt.average = parseFloat((all.reduce((a, b) => a + b, 0) / all.length).toFixed(3));
    pt.error = parseFloat((pt.average - pt.nominal).toFixed(3));
  }
  // Hystereze = max |VZ − SE| přes série (ne jen série[0]).
  let h = 0;
  pt.series.forEach((s) => {
    if (s.ascending && s.descending) h = Math.max(h, Math.abs(s.ascending - s.descending));
  });
  pt.hysteresis = parseFloat(h.toFixed(3));
  return pt;
}

// Vodicí sekvence: jedna série = celý vzestup (k=0..n-1) pak celý sestup. Pak další série.
function nextStep(step, n, seriesCount) {
  const k = step.k + 1;
  if (k < n) return { ...step, k };
  if (step.phase === PHASE.ASC) return { seriesIdx: step.seriesIdx, phase: PHASE.DESC, k: 0 };
  if (step.seriesIdx + 1 < seriesCount) return { seriesIdx: step.seriesIdx + 1, phase: PHASE.ASC, k: 0 };
  return null; // hotovo
}
function ptIdxForStep(step, n) {
  return step.phase === PHASE.ASC ? step.k : n - 1 - step.k;
}

export default function CalibrationRunScreen() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [cal, setCal] = useState(null);
  const [methodology, setMethodology] = useState(null);
  const [measurements, setMeasurements] = useState({ points: [] });
  const [measuring, setMeasuring] = useState(false);
  const [connState, setConnState] = useState(ConnectionState.DISCONNECTED);
  const [live, setLive] = useState(null); // poslední reading { value, unit, status }
  // Výchozí je ruční režim — měření (teplota, tlak, vlhkost…) nevyžaduje připojení
  // váhy. Auto (váha přes USB) je volitelné, laborant si ho zapne jen když měří
  // hmotnost a má připojený indikátor.
  const [mode, setMode] = useState('manual'); // 'auto' (váha) | 'manual' (ruční)
  const [prompt, setPrompt] = useState(null); // { kind: 'zero'|'place'|'remove'|'done'|'skip', nominal }
  const [unitWarn, setUnitWarn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  // Soft gate při dokončení: když je výsledek „Neposouzeno" (chybí MPE),
  // nabídneme doplnit MPE v metodice místo tichého uložení bez výroku o shodě.
  const [showUnassessedDialog, setShowUnassessedDialog] = useState(false);
  // Podmínky prostředí + výsledek — povinné pro kompletní kalibrační list
  // (completeness gate při schválení). Zadávají se zde před odesláním.
  const [env, setEnv] = useState({ temperature: '', humidity: '' });
  const [decisionRule, setDecisionRule] = useState('simple'); // rozhodovací pravidlo laboratoře
  // Zvýraznění sekce podmínek prostředí při příchodu z odkazu (#env-conditions),
  // např. z completeness gate ve schvalování — navede laboranta přímo na pole.
  const [highlightEnv, setHighlightEnv] = useState(false);
  const envSectionRef = useRef(null);

  const providerRef = useRef(null);
  if (!providerRef.current) providerRef.current = createInstrumentProvider();

  const measuringRef = useRef(false);
  const stepRef = useRef(null);
  const gateRef = useRef(null);
  const awaitingZeroRef = useRef(false);
  const measurementsRef = useRef(measurements);
  const seriesCountRef = useRef(3);
  const fnRef = useRef({});
  const persistTimerRef = useRef(null);

  useEffect(() => {
    measurementsRef.current = measurements;
    seriesCountRef.current = measurements.points[0]?.series?.length || 3;
  }, [measurements]);

  // Příchod z odkazu „#env-conditions" (z completeness gate) → odscrolluj na pole
  // podmínek prostředí a krátce je zvýrazni, ať je laborant hned najde.
  useEffect(() => {
    if (!cal) return;
    if (window.location.hash !== '#env-conditions' || !envSectionRef.current) return;
    envSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightEnv(true);
    const timer = setTimeout(() => setHighlightEnv(false), 3000);
    return () => clearTimeout(timer);
  }, [cal]);

  const isMass = cal?.laboratory === 'hmotnost';
  const unitSymbol = cal?.probeUnitSymbol || 'g';

  // Předepsané (jmenovité) podmínky prostředí z metodiky — pro zobrazení
  // rozsahu u polí a varování při hodnotě mimo rozsah.
  const prescribed = methodology?.extractedData?.environmentalConditions || {};
  const rangeText = (b) => (b && (b.min != null || b.max != null))
    ? `${b.min ?? '−∞'}–${b.max ?? '∞'}` : null;
  const outOfRange = (val, b) => {
    if (val === '' || val == null || !b) return false;
    const n = parseFloat(val);
    if (Number.isNaN(n)) return false;
    return (b.min != null && n < b.min) || (b.max != null && n > b.max);
  };
  const tempRange = rangeText(prescribed.temperature);
  const humRange = rangeText(prescribed.humidity);
  const tempBad = outOfRange(env.temperature, prescribed.temperature);
  const humBad = outOfRange(env.humidity, prescribed.humidity);
  // Podmínky prostředí jsou POVINNÉ — bez nich nelze kalibraci odeslat ke
  // schválení (backend to i tak odmítne). Prázdná pole označíme a navigujeme.
  const tempEmpty = String(env.temperature).trim() === '';
  const humEmpty = String(env.humidity).trim() === '';
  const envMissing = tempEmpty || humEmpty;

  // Dovolená chyba (MPE) z metodiky → VÝSLEDEK se počítá z naměřených chyb,
  // nenastavuje se ručně. Bez MPE se shoda neposuzuje (result = 'unassessed'),
  // list pak netvrdí vyhovuje/nevyhovuje. (mirror backendu _mpe_for)
  const tcMpe = methodology?.extractedData?.toleranceCriteria || null;
  const mpeFor = (nominal) => {
    if (!tcMpe) return null;
    const value = parseFloat(tcMpe.value);
    if (!Number.isFinite(value) || value <= 0) return null;
    const mode = (tcMpe.mode || 'abs').toLowerCase();
    const nom = Number.isFinite(parseFloat(nominal)) ? Math.abs(parseFloat(nominal)) : 0;
    if (mode === 'pctreading' || mode === 'pct') return (value / 100) * nom;
    if (mode === 'pctfs' || mode === 'fs') {
      const fs = parseFloat(tcMpe.fsValue);
      return Number.isFinite(fs) ? (value / 100) * Math.abs(fs) : null;
    }
    return Math.abs(value);
  };
  // Rozhodovací pravidlo laboratoře. Guard band zpřísní limit o rozšířenou
  // nejistotu U: |chyba| ≤ MPE − U. U je známé až po výpočtu nejistoty — do té
  // doby je badge „předběžný" (počítá prostým přijetím a dá to najevo).
  const expandedU = Number(cal?.uncertaintyResults?.expanded);
  const hasU = Number.isFinite(expandedU) && expandedU > 0;
  const guardBand = decisionRule === 'guard_band';
  const preliminary = guardBand && !hasU;  // guard band zvolen, ale U ještě nemáme
  const limitFor = (nominal) => {
    const m = mpeFor(nominal);
    if (m == null) return null;
    return (guardBand && hasU) ? Math.max(0, m - expandedU) : m;
  };
  const pointPass = (measurements.points || []).map((p) => {
    const lim = limitFor(p.nominal);
    if (lim == null) return null;
    return Math.abs(Number(p.error) || 0) <= lim;
  });
  const anyEval = pointPass.some((c) => c !== null);
  const failCount = pointPass.filter((c) => c === false).length;
  const computedResult = anyEval ? (failCount === 0 ? 'pass' : 'fail') : 'unassessed';

  // Detaily neshody: pro každý bod mimo limit vrať naměřené hodnoty a o kolik
  // MPE překračuje — aby bylo vidět PROČ list nevyhovuje a dalo se posoudit,
  // zda upravit referenční rámec metodiky (body / MPE).
  const failDetails = (measurements.points || []).map((p) => {
    const lim = limitFor(p.nominal);
    if (lim == null) return null;
    const err = Number(p.error) || 0;
    if (Math.abs(err) <= lim) return null;
    return { nominal: p.nominal, error: err, limit: lim, over: Math.abs(err) - lim };
  }).filter(Boolean);

  const hasPermission = useCallback((action, lab) => {
    if (!user) return false;
    if (user.isAdmin) return true;
    const perm = (user.permissions || []).find((p) => p.module === lab);
    return perm?.actions?.includes(action) || false;
  }, [user]);

  // ── Perzistence rozpracovaných měření (proti ztrátě při refresh/odpojení) ──
  const doPersist = useCallback(async () => {
    try {
      await api.put(`/calibrations/${id}`, { measurements: measurementsRef.current });
    } catch (_e) {
      /* tiché — finální uložení řeší "Uložit a zavřít" */
    }
  }, [id]);

  const schedulePersist = useCallback(() => {
    if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    persistTimerRef.current = setTimeout(() => doPersist(), 1500);
  }, [doPersist]);

  // ── Vodicí controller (přes refs → stabilní closury, žádné re-subscribe) ──
  const enterStep = useCallback((step) => {
    if (!step) {
      // hotovo
      measuringRef.current = false;
      setMeasuring(false);
      if (gateRef.current) gateRef.current.disarm();
      providerRef.current.stop();
      setPrompt({ kind: 'done' });
      toast.success(t('calibration-run.toast-measurement-done'));
      doPersist();
      return;
    }
    stepRef.current = step;
    const n = measurementsRef.current.points.length;
    const ptIdx = ptIdxForStep(step, n);
    const pt = measurementsRef.current.points[ptIdx];
    if (!pt) return;
    if (pt.outOfRange) {
      // Bod mimo rozsah — přeskočit (nezachytávat, vyloučit z certifikátu).
      setPrompt({ kind: 'skip', nominal: pt.nominal });
      setTimeout(() => fnRef.current.advanceStep(), 400);
      return;
    }
    setPrompt({ kind: step.phase === PHASE.ASC ? 'place' : 'remove', nominal: pt.nominal });
    if (providerRef.current.isMock && providerRef.current.setSimulatedWeight) {
      providerRef.current.setSimulatedWeight(pt.nominal);
    }
    if (gateRef.current) gateRef.current.arm();
  }, [t, doPersist]);

  const advanceStep = useCallback(() => {
    const n = measurementsRef.current.points.length;
    enterStep(nextStep(stepRef.current, n, seriesCountRef.current));
  }, [enterStep]);

  const captureValue = useCallback((value, source) => {
    const step = stepRef.current;
    if (!step) return;
    const cur = measurementsRef.current;
    const n = cur.points.length;
    const ptIdx = ptIdxForStep(step, n);
    const pts = cur.points.map((p) => ({ ...p, series: p.series.map((s) => ({ ...s })) }));
    const ser = pts[ptIdx].series[step.seriesIdx];
    ser[step.phase] = value;
    // Provenance per série/směr (backend schemaless — extra klíče projdou; GUM čte jen asc/desc).
    ser[step.phase === PHASE.ASC ? 'srcAscending' : 'srcDescending'] = source;
    ser[step.phase === PHASE.ASC ? 'tsAscending' : 'tsDescending'] = new Date().toISOString();
    recomputePoint(pts[ptIdx]);
    const ident = providerRef.current.identity || null;
    const next = { ...cur, points: pts, ...(ident ? { device: ident } : {}) };
    measurementsRef.current = next;
    setMeasurements(next);
    schedulePersist();
    advanceStep();
  }, [advanceStep, schedulePersist]);

  const markOutOfRangeAndAdvance = useCallback(() => {
    const step = stepRef.current;
    if (!step) return;
    const cur = measurementsRef.current;
    const ptIdx = ptIdxForStep(step, cur.points.length);
    const pts = cur.points.map((p, i) => (i === ptIdx ? { ...p, outOfRange: true } : p));
    const next = { ...cur, points: pts };
    measurementsRef.current = next;
    setMeasurements(next);
    toast.warning(t('calibration-run.overweight-skip', { n: cur.points[ptIdx].nominal, unit: unitSymbol }));
    if (gateRef.current) gateRef.current.disarm();
    advanceStep();
  }, [advanceStep, t, unitSymbol]);

  const handleReading = useCallback((reading) => {
    setLive(reading);
    setUnitWarn(reading.unit != null && reading.unit !== UNIT.GRAM);
    if (!measuringRef.current) return;
    if (awaitingZeroRef.current) {
      if (reading.status === STATUS.STABLE_ZERO) {
        awaitingZeroRef.current = false;
        stepRef.current = { seriesIdx: 0, phase: PHASE.ASC, k: 0 };
        enterStep(stepRef.current);
      }
      return;
    }
    if (gateRef.current) gateRef.current.feed(reading);
  }, [enterStep]);

  // Drž aktuální handlery v ref (subscription/gate je čte, neresubscribuje).
  fnRef.current = { handleReading, captureValue, markOutOfRangeAndAdvance, advanceStep };

  // ── Mount: subscription na provider + gate ──
  useEffect(() => {
    const provider = providerRef.current;
    const gate = createStabilityGate({ n: 5, lsb: 1, timeoutMs: 15000 })
      .on('accepted', ({ value }) => fnRef.current.captureValue(value, 'hid'))
      .on('overweight', () => fnRef.current.markOutOfRangeAndAdvance())
      .on('fault', () => toast.error(t('calibration-run.fault-warning')))
      .on('timeout', () => toast.warning(t('calibration-run.timeout-warning')));
    gateRef.current = gate;

    const unsubReading = provider.subscribe((r) => fnRef.current.handleReading(r));
    const unsubState = provider.subscribeState(setConnState);
    // Tichý reconnect na dříve povolené zařízení (jen HID).
    if (typeof provider.tryReconnect === 'function') provider.tryReconnect();

    return () => {
      unsubReading();
      unsubState();
      gate.disarm();
      provider.stop();
      if (typeof provider.disconnect === 'function') provider.disconnect();
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Init: fetch kalibrace + metodika, validace prereqs ──
  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        const r = await api.get(`/calibrations/${id}`);
        if (cancelled) return;
        const c = r.data;
        if (!['pending_intake', 'in_progress'].includes(c.status)) {
          toast.warning(t('calibration-run.warn-not-in-progress'));
          navigate(`/calibrations/${id}`);
          return;
        }
        if (!hasPermission('calibrate', c.laboratory)) {
          toast.error(t('calibration-run.error-no-permission'));
          navigate(`/calibrations/${id}`);
          return;
        }
        const missing = [];
        if (!c.probeId) missing.push(t('calibration-run.missing-probe'));
        if (!c.methodology) missing.push(t('calibration-run.missing-methodology'));
        if (!c.etalon) missing.push(t('calibration-run.missing-etalon'));
        if (missing.length > 0) {
          toast.warning(t('calibration-run.missing-prereq', { fields: missing.join(', ') }));
          navigate(`/calibrations/${id}`);
          return;
        }
        setCal(c);
        const ec = c.environmentalConditions || {};
        setEnv({ temperature: ec.temperature ?? '', humidity: ec.humidity ?? '' });
        if (c.measurements?.points?.length > 0) {
          setMeasurements(c.measurements);
        }
        // Rozhodovací pravidlo laboratoře (pro živé posouzení shody).
        api.get('/lab-settings')
          .then((r) => { if (!cancelled) setDecisionRule(r.data?.decisionRule || 'simple'); })
          .catch(() => {});
        try {
          const mr = await api.get(`/methodologies/${c.methodology}`);
          if (cancelled) return;
          setMethodology(mr.data);
          if (!(c.measurements?.points?.length > 0)) {
            const pts = mr.data?.extractedData?.calibrationPoints?.points || [];
            const seriesCount = mr.data?.extractedData?.series?.count || 3;
            if (pts.length === 0) {
              toast.error(t('calibration-run.error-methodology-no-points'));
              navigate(`/calibrations/${id}`);
              return;
            }
            setMeasurements({
              points: pts.map((nominal) => ({
                nominal,
                series: Array.from({ length: seriesCount }, () => ({ ascending: 0, descending: 0 })),
                average: 0,
                error: 0,
                hysteresis: 0,
              })),
            });
          }
        } catch {
          toast.error(t('calibration-run.error-methodology-load'));
          navigate(`/calibrations/${id}`);
        }
      } catch {
        toast.error(t('calibration-run.error-cal-not-found'));
        navigate('/calibrations');
      }
    }
    init();
    return () => { cancelled = true; };
  }, [id, navigate, hasPermission, t]);

  // Když přepnu na manuální režim, default simulátor/HID nepotřebuju (ruční edit je vždy dostupný).

  const connectScale = async () => {
    setConnecting(true);
    try {
      await providerRef.current.connect();
      toast.success(t('calibration-run.connected'));
    } catch (err) {
      toast.error(err?.message || t('calibration-run.connect-error'));
    } finally {
      setConnecting(false);
    }
  };

  const startMeasuring = () => {
    if (measurements.points.length === 0) {
      toast.error(t('calibration-run.error-no-points-to-measure'));
      return;
    }
    if (mode === 'auto' && !providerRef.current.isConnected) {
      toast.warning(t('calibration-run.not-connected-banner'));
      return;
    }
    measuringRef.current = true;
    setMeasuring(true);
    providerRef.current.start();
    if (mode === 'auto') {
      // Vynulovat prázdnou váhu na začátku měření (status Stable-at-Zero).
      awaitingZeroRef.current = true;
      setPrompt({ kind: 'zero' });
    } else {
      // Ruční režim — bez vodicí sekvence; laborant zapisuje do tabulky.
      measuringRef.current = false;
      setMeasuring(false);
      setPrompt(null);
      toast.info(t('calibration-run.manual-hint'));
    }
  };

  const stopMeasuring = () => {
    measuringRef.current = false;
    awaitingZeroRef.current = false;
    setMeasuring(false);
    if (gateRef.current) gateRef.current.disarm();
    providerRef.current.stop();
    setPrompt(null);
  };

  // Ruční editace buňky — vždy dostupný fallback. Označí původ 'manual'.
  const updatePoint = (ptIdx, serIdx, field, value) => {
    const cur = measurementsRef.current;
    const pts = cur.points.map((p) => ({ ...p, series: p.series.map((s) => ({ ...s })) }));
    const ser = pts[ptIdx].series[serIdx];
    ser[field] = parseFloat(value) || 0;
    ser[field === PHASE.ASC ? 'srcAscending' : 'srcDescending'] = 'manual';
    recomputePoint(pts[ptIdx]);
    const next = { ...cur, points: pts };
    measurementsRef.current = next;
    setMeasurements(next);
  };

  // DEMO: vygeneruje realistické měřené hodnoty pro všechny body (nominál +
  // malý náhodný šum), ať se dá demo projet bez ručního vyplňování / připojené
  // váhy. Ascending/descending se mírně liší → vznikne i nenulová hystereze.
  const fillDemoValues = () => {
    const cur = measurementsRef.current;
    if (!cur.points.length) {
      toast.error(t('calibration-run.error-no-points-to-measure'));
      return;
    }
    const pts = cur.points.map((p) => {
      if (p.outOfRange) return p;
      const jitter = Math.abs(p.nominal) * 0.003 + 0.05; // ~0,3 % + malý absolutní šum
      const series = p.series.map((s) => ({
        ...s,
        ascending: parseFloat((p.nominal + (Math.random() - 0.5) * jitter).toFixed(3)),
        descending: parseFloat((p.nominal + (Math.random() - 0.5) * jitter).toFixed(3)),
        srcAscending: 'demo',
        srcDescending: 'demo',
      }));
      const np = { ...p, series };
      recomputePoint(np);
      return np;
    });
    const next = { ...cur, points: pts };
    measurementsRef.current = next;
    setMeasurements(next);
    toast.success('Vygenerovány demo hodnoty měření');
  };

  // Kliknutí na „Uložit a zavřít". Je-li výsledek neposouzen (chybí MPE),
  // otevři soft gate; jinak rovnou ulož a odejdi.
  const saveAndExit = () => {
    if (computedResult === 'unassessed') {
      setShowUnassessedDialog(true);
      return;
    }
    runSave();
  };

  // Odkaz z dialogu do metodiky: nejprve ulož rozpracované měření (ať se
  // práce neztratí), pak otevři editaci metodiky s předvybranou laboratoří.
  const goFixMpe = async () => {
    setShowUnassessedDialog(false);
    stopMeasuring();
    if (cal) {
      setSaving(true);
      try {
        await api.put(`/calibrations/${id}`, {
          measurements: measurementsRef.current,
          environmentalConditions: {
            temperature: env.temperature === '' ? null : parseFloat(env.temperature),
            humidity: env.humidity === '' ? null : parseFloat(env.humidity),
          },
          result: computedResult,
          ...(cal.status === 'pending_intake' ? { status: 'in_progress' } : {}),
        });
        toast.success(t('calibration-run.toast-progress-saved'));
      } catch (err) {
        toastApiError(err, t('calibration-run.error-save'));
      } finally {
        setSaving(false);
      }
    }
    const mid = methodology?.id || cal?.methodology || '';
    const lab = methodology?.laboratory || '';
    navigate(`/methodology?select=${encodeURIComponent(mid)}&lab=${encodeURIComponent(lab)}`);
  };

  const runSave = async () => {
    stopMeasuring();
    if (!cal) return;
    // Varuj, když jsou podmínky prostředí mimo předepsaný rozsah metodiky.
    if (tempBad || humBad) {
      const parts = [];
      if (tempBad) parts.push(`teplota mimo ${tempRange} °C`);
      if (humBad) parts.push(`vlhkost mimo ${humRange} %RV`);
      if (!window.confirm(`Podmínky prostředí jsou mimo předepsaný rozsah (${parts.join(', ')}). Uložit i tak?`)) return;
    }
    setSaving(true);
    try {
      const payload = {
        measurements: measurementsRef.current,
        environmentalConditions: {
          temperature: env.temperature === '' ? null : parseFloat(env.temperature),
          humidity: env.humidity === '' ? null : parseFloat(env.humidity),
        },
        result: computedResult,
        ...(cal.status === 'pending_intake' ? { status: 'in_progress' } : {}),
      };
      await api.put(`/calibrations/${id}`, payload);
      const inProgress = ['in_progress', 'pending_intake'].includes(cal.status);
      if (inProgress && envMissing) {
        // Uloženo jako rozpracované, ale chybí povinné podmínky prostředí →
        // odeslat ke schválení nelze. Nenabízíme submit, jen srozumitelně
        // informujeme, že jde o povinná pole.
        toast.warning(t('calibration-run.env-required-to-submit'));
      } else if (inProgress) {
        toast.success(t('calibration-run.toast-saved'), {
          action: {
            label: t('calibration-run.toast-action-submit'),
            onClick: async () => {
              try {
                await api.put(`/calibrations/${id}/submit-approval`);
                toast.success(t('calibration-run.toast-submitted'));
                navigate('/approvals');
              } catch (err) {
                toastApiError(err, t('calibration-run.error-submit'));
              }
            },
          },
        });
      } else {
        toast.success(t('calibration-run.toast-saved'));
      }
      navigate(`/calibrations/${id}`);
    } catch (err) {
      toastApiError(err, t('calibration-run.error-save'));
    } finally {
      setSaving(false);
    }
  };

  const closeWithoutSaving = () => {
    stopMeasuring();
    navigate(`/calibrations/${id}`);
  };

  if (!cal) {
    return (
      <div className="fixed inset-0 flex items-center justify-center" style={{ background: 'var(--ct-bg)' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-3" style={{ borderColor: 'var(--ct-accent)', borderTopColor: 'transparent' }} />
          <p className="text-sm" style={{ color: 'var(--ct-text-secondary)' }}>{t('calibration-run.loading')}</p>
        </div>
      </div>
    );
  }

  const seriesCount = measurements.points[0]?.series?.length || 3;
  const connected = connState === ConnectionState.CONNECTED;

  // Etalon displej = aktuální cílový bod; přístroj displej = živý odečet.
  const etalonDisplay = prompt && prompt.nominal != null ? String(prompt.nominal) : '—';
  const liveValue = live && live.value != null ? Number(live.value).toFixed(0) : '—';
  const liveStatusText = (() => {
    if (!live) return t('calibration-run.display-disconnected');
    switch (live.status) {
      case STATUS.STABLE: return t('calibration-run.live-stable');
      case STATUS.STABLE_ZERO: return t('calibration-run.live-zero');
      case STATUS.IN_MOTION: return t('calibration-run.live-motion');
      case STATUS.OVER_WEIGHT: return t('calibration-run.live-overweight');
      default: return t('calibration-run.live-unknown');
    }
  })();

  const promptText = (() => {
    if (!prompt) return null;
    switch (prompt.kind) {
      case 'zero': return t('calibration-run.prompt-zero');
      case 'place': return t('calibration-run.prompt-place', { n: prompt.nominal, unit: unitSymbol });
      case 'remove': return t('calibration-run.prompt-remove', { n: prompt.nominal, unit: unitSymbol });
      case 'skip': return t('calibration-run.prompt-skip', { n: prompt.nominal, unit: unitSymbol });
      case 'done': return t('calibration-run.prompt-done');
      default: return null;
    }
  })();

  return (
    <div data-testid="calibration-run-screen" className="fixed inset-0 z-50 flex flex-col" style={{ background: 'var(--ct-bg)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b" style={{ borderColor: 'var(--ct-border)', background: 'var(--ct-surface)' }}>
        <div className="flex items-center gap-3">
          <FlaskConical className="w-5 h-5" style={{ color: 'var(--ct-accent)' }} />
          <span className="font-bold" style={{ fontFamily: 'Inter', color: 'var(--ct-accent)' }}>
            {t('calibration-run.header-title')} | {(cal.laboratory || '').toUpperCase()}
          </span>
          {cal.methodologyCode && (
            <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(var(--ct-accent-rgb),0.15)', color: 'var(--ct-accent)' }}>
              {cal.methodologyCode}
            </span>
          )}
          <span className="text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
            {cal.instrumentName} {cal.instrumentType ? `· ${cal.instrumentType}` : ''}
          </span>
          {cal.probeCode && (
            <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ background: 'rgba(var(--ct-accent-rgb),0.08)', color: 'var(--ct-accent)' }} title={cal.probeName || ''}>
              {t('calibration-run.probe-prefix')} {cal.probeCode}
              {(cal.probeRangeMin !== undefined && cal.probeRangeMax !== undefined) && (
                <span className="ml-1" style={{ color: 'var(--ct-text-secondary)' }}>{cal.probeRangeMin}–{cal.probeRangeMax} {cal.probeUnitSymbol || ''}</span>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Režim: automaticky (váha) / ruční — auto (připojení váhy přes USB)
              nabízíme JEN u hmotnosti. U ostatních veličin (teplota, tlak…) je
              vždy jen ruční zadání, žádná váha se nevyžaduje ani nenabízí. */}
          {isMass && (
            <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
              <span>{t('calibration-run.mode-label')}</span>
              <Button onClick={() => setMode('auto')} className={mode === 'auto' ? 'ct-btn-primary' : 'ct-btn-secondary'} style={{ height: 28, padding: '0 8px' }}>
                <Usb className="w-3.5 h-3.5" /> {t('calibration-run.mode-auto')}
              </Button>
              <Button onClick={() => setMode('manual')} className={mode === 'manual' ? 'ct-btn-primary' : 'ct-btn-secondary'} style={{ height: 28, padding: '0 8px' }}>
                <Hand className="w-3.5 h-3.5" /> {t('calibration-run.mode-manual')}
              </Button>
            </div>
          )}

          {mode === 'auto' && !connected && (
            <Button data-testid="connect-scale-btn" onClick={connectScale} disabled={connecting} className="ct-btn-primary gap-1">
              <Usb className="w-4 h-4" /> {connecting ? t('calibration-run.connecting') : t('calibration-run.connect-btn')}
            </Button>
          )}

          {!measuring ? (
            <Button data-testid="start-sim-btn" onClick={startMeasuring} className="gap-1" style={{ background: 'var(--ct-success)', color: '#fff' }} disabled={mode === 'auto' && !connected}>
              <Zap className="w-4 h-4" /> {t('calibration-run.btn-start-measurement')}
              {providerRef.current?.isMock && (<span className="ml-1 text-[10px] opacity-80">{t('calibration-run.demo-suffix')}</span>)}
            </Button>
          ) : (
            <Button data-testid="stop-sim-btn" onClick={stopMeasuring} className="gap-1" style={{ background: 'var(--ct-error)', color: '#fff' }}>
              <Square className="w-4 h-4" /> {t('calibration-run.btn-stop-measurement')}
            </Button>
          )}

          <Button data-testid="demo-fill-btn" onClick={fillDemoValues} className="ct-btn-secondary gap-1" title="Vyplní všechny body simulovanými hodnotami (pro demo)">
            <FlaskConical className="w-4 h-4" /> Demo: vygenerovat hodnoty
          </Button>

          {/* Podmínky prostředí + výsledek — povinné pro kompletní KL. */}
          <div
            id="env-conditions"
            ref={envSectionRef}
            className="flex items-center gap-2 pl-3 ml-1 rounded transition-all"
            style={highlightEnv
              ? { borderLeft: '1px solid var(--ct-border)', boxShadow: '0 0 0 2px var(--ct-warning)', background: 'rgba(234,179,8,0.12)' }
              : { borderLeft: '1px solid var(--ct-border)' }}
          >
            <label className="flex items-center gap-1 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
              {t('calibration-run.env-temperature')}
              <span style={{ color: 'var(--ct-error)' }} title={t('calibration-run.required-field')}>*</span>
              <Input
                data-testid="env-temperature"
                type="number"
                value={env.temperature}
                onChange={(e) => setEnv((p) => ({ ...p, temperature: e.target.value }))}
                className="h-8 w-16 text-sm"
                placeholder="°C"
                style={tempEmpty ? { borderColor: 'var(--ct-error)' } : tempBad ? { borderColor: 'var(--ct-warning)' } : undefined}
                title={tempEmpty ? t('calibration-run.required-field') : tempBad ? `Mimo předepsaný rozsah ${tempRange} °C` : undefined}
              />
              {tempRange && <span className="opacity-70">({tempRange})</span>}
            </label>
            <label className="flex items-center gap-1 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
              {t('calibration-run.env-humidity')}
              <span style={{ color: 'var(--ct-error)' }} title={t('calibration-run.required-field')}>*</span>
              <Input
                data-testid="env-humidity"
                type="number"
                value={env.humidity}
                onChange={(e) => setEnv((p) => ({ ...p, humidity: e.target.value }))}
                className="h-8 w-16 text-sm"
                placeholder="%RH"
                style={humEmpty ? { borderColor: 'var(--ct-error)' } : humBad ? { borderColor: 'var(--ct-warning)' } : undefined}
                title={humEmpty ? t('calibration-run.required-field') : humBad ? `Mimo předepsaný rozsah ${humRange} %RV` : undefined}
              />
              {humRange && <span className="opacity-70">({humRange})</span>}
            </label>
            <label className="flex items-center gap-1 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>
              {t('calibration-run.result-label')}
              {/* Výsledek se POČÍTÁ z chyb vs. MPE (prosté přijetí). Bez MPE
                  v metodice → „Neposouzeno" (žádné tiché vyhovuje). */}
              <span
                data-testid="result-computed"
                className="inline-flex items-center gap-1 h-8 px-2.5 rounded text-xs font-semibold"
                style={
                  computedResult === 'pass'
                    ? { background: 'var(--ct-success-soft)', color: 'var(--ct-success)' }
                    : computedResult === 'fail'
                    ? { background: 'var(--ct-error-soft)', color: 'var(--ct-error)' }
                    : { background: 'var(--ct-bg-secondary)', color: 'var(--ct-text-secondary)' }
                }
                title={
                  computedResult === 'unassessed'
                    ? t('calibration-run.result-unassessed-hint')
                    : t('calibration-run.result-computed-hint')
                }
              >
                {computedResult === 'pass'
                  ? t('calibration-run.result-pass')
                  : computedResult === 'fail'
                  ? `${t('calibration-run.result-fail')} · ${failCount}×`
                  : t('calibration-run.result-unassessed')}
              </span>
              {computedResult !== 'unassessed' && (
                <span className="opacity-70" style={{ fontSize: '0.7rem' }}>
                  {guardBand ? t('calibration-run.result-rule-guard') : t('calibration-run.result-rule-simple')}
                  {preliminary ? ` · ${t('calibration-run.result-preliminary')}` : ''}
                </span>
              )}
            </label>
          </div>

          <Button data-testid="save-calibration-btn" onClick={saveAndExit} disabled={saving} className="ct-btn-primary gap-1">
            <Save className="w-4 h-4" /> {saving ? t('calibration-run.btn-saving') : t('calibration-run.btn-save-exit')}
          </Button>
          <Button onClick={closeWithoutSaving} className="ct-btn-secondary">{t('calibration-run.btn-close-no-save')}</Button>
        </div>
      </div>

      {/* Soft gate: výsledek „Neposouzeno" (chybí MPE v metodice). */}
      <Dialog open={showUnassessedDialog} onOpenChange={setShowUnassessedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('calibration-run.unassessed-dialog-title')}</DialogTitle>
            <DialogDescription>{t('calibration-run.unassessed-dialog-desc')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              className="ct-btn-secondary"
              onClick={() => { setShowUnassessedDialog(false); runSave(); }}
            >
              {t('calibration-run.unassessed-dialog-continue')}
            </Button>
            <Button className="ct-btn-primary" onClick={goFixMpe}>
              {t('calibration-run.unassessed-dialog-fix')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stavový / vodicí banner */}
      {(promptText || (mode === 'auto' && !connected) || unitWarn) && (
        <div className="px-6 py-2 flex items-center gap-2 text-sm" style={{ background: 'var(--ct-surface)', borderBottom: '1px solid var(--ct-border)' }}>
          {unitWarn && (
            <span className="flex items-center gap-1" style={{ color: 'var(--ct-error)' }}>
              <AlertTriangle className="w-4 h-4" /> {t('calibration-run.unit-warning')}
            </span>
          )}
          {!unitWarn && mode === 'auto' && !connected && (
            <span className="flex items-center gap-1" style={{ color: 'var(--ct-warning)' }}>
              <AlertTriangle className="w-4 h-4" /> {t('calibration-run.not-connected-banner')}
            </span>
          )}
          {!unitWarn && promptText && (
            <span style={{ color: 'var(--ct-accent)', fontWeight: 600 }}>{promptText}</span>
          )}
        </div>
      )}

      {/* Detaily neshody: proč list nevyhovuje + konkrétní hodnoty. */}
      {computedResult === 'fail' && failDetails.length > 0 && (
        <div className="px-6 py-3" style={{ background: 'var(--ct-error-soft)', borderBottom: '1px solid var(--ct-border)' }}>
          <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: 'var(--ct-error)' }}>
            <AlertTriangle className="w-4 h-4" />
            {t('calibration-run.fail-detail-title', { n: failDetails.length })}
            <span className="font-normal opacity-80">
              {t('calibration-run.fail-detail-rule', {
                rule: guardBand ? t('calibration-run.result-rule-guard') : t('calibration-run.result-rule-simple'),
              })}
            </span>
          </div>
          <table className="mt-2 text-xs" style={{ color: 'var(--ct-text)' }}>
            <thead style={{ color: 'var(--ct-text-secondary)' }}>
              <tr className="text-left">
                <th className="pr-4 font-normal">{t('calibration-run.fail-detail-col-nominal')}</th>
                <th className="pr-4 font-normal">{t('calibration-run.fail-detail-col-error')}</th>
                <th className="pr-4 font-normal">{t('calibration-run.fail-detail-col-limit')}</th>
                <th className="pr-4 font-normal">{t('calibration-run.fail-detail-col-over')}</th>
              </tr>
            </thead>
            <tbody>
              {failDetails.map((d, i) => (
                <tr key={i}>
                  <td className="pr-4 py-0.5">{d.nominal} {unitSymbol}</td>
                  <td className="pr-4 py-0.5 font-semibold">{d.error > 0 ? '+' : ''}{d.error.toFixed(3)} {unitSymbol}</td>
                  <td className="pr-4 py-0.5">±{d.limit.toFixed(3)} {unitSymbol}</td>
                  <td className="pr-4 py-0.5 font-semibold" style={{ color: 'var(--ct-error)' }}>+{d.over.toFixed(3)} {unitSymbol}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            type="button"
            onClick={goFixMpe}
            className="mt-2 text-xs underline"
            style={{ color: 'var(--ct-accent)' }}
          >
            {t('calibration-run.fail-detail-edit-methodology')}
          </button>
        </div>
      )}

      {/* Large displays */}
      <div className="flex gap-6 p-6">
        <div className="flex-1 text-center p-8 rounded-lg" style={{ background: 'var(--ct-surface)', border: '1px solid var(--ct-border)', boxShadow: 'var(--ct-shadow-sm)' }}>
          <div className="text-xs uppercase tracking-wider mb-3" style={{ color: 'var(--ct-text-secondary)' }}>{t('calibration-run.display-etalon')}</div>
          <div className="ct-large-display transition-all duration-200" style={{ color: 'var(--ct-accent)' }}>{etalonDisplay}</div>
          <div className="mt-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{cal.etalonCode || ''} {unitSymbol}</div>
        </div>
        <div className="flex-1 text-center p-8 rounded-lg" style={{ background: 'var(--ct-surface)', border: '1px solid var(--ct-border)', boxShadow: 'var(--ct-shadow-sm)' }}>
          <div className="text-xs uppercase tracking-wider mb-3" style={{ color: 'var(--ct-text-secondary)' }}>{t('calibration-run.display-instrument')}</div>
          <div className="ct-large-display transition-all duration-200" style={{ color: live && live.status === STATUS.STABLE ? 'var(--ct-success)' : 'var(--ct-warning)' }}>{liveValue}</div>
          <div className="mt-2 text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{liveStatusText} · {cal.instrumentName || ''}</div>
        </div>
      </div>

      {/* Measurement table */}
      <div className="flex-1 overflow-auto px-6 pb-4">
        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--ct-border)' }}>
              <th className="py-2 px-2 text-left text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('calibration-run.table-col-segments')}</th>
              {Array.from({ length: seriesCount }, (_, i) => (
                <th key={i} className="py-2 px-2 text-center text-xs" style={{ color: 'var(--ct-text-secondary)' }} colSpan={2}>{t('calibration-run.table-col-series', { n: i + 1 })}</th>
              ))}
              <th className="py-2 px-2 text-center text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('calibration-run.table-col-average')}</th>
              <th className="py-2 px-2 text-center text-xs" style={{ color: 'var(--ct-text-secondary)' }}>{t('calibration-run.table-col-error')}</th>
            </tr>
          </thead>
          <tbody>
            {measurements.points.map((pt, ptIdx) => {
              const errorAbs = Math.abs(pt.error || 0);
              // Pass/fail práh NEAPLIKOVAT na hmotnost (MPE není v KP HM1 definované — viz PRD Won't).
              const isOk = isMass ? null : errorAbs < 0.5;
              const okBg = isOk === null ? undefined : (isOk ? 'var(--ct-success-soft)' : 'var(--ct-error-soft)');
              const okBorder = isOk === null ? undefined : (isOk ? 'var(--ct-success)' : 'var(--ct-error)');
              return (
                <tr key={ptIdx} className="ct-table-row" style={{ borderBottom: '1px solid var(--ct-border)', opacity: pt.outOfRange ? 0.5 : 1 }}>
                  <td className="py-1 px-2 font-mono font-bold text-center" style={{ color: 'var(--ct-accent)', width: '120px' }}>
                    {pt.nominal}
                    {pt.outOfRange && (<span className="ml-1 text-[10px]" style={{ color: 'var(--ct-error)' }}>{t('calibration-run.out-of-range-tag')}</span>)}
                  </td>
                  {pt.series.map((ser, serIdx) => (
                    <td key={serIdx} className="py-1 px-1" colSpan={2}>
                      <div className="flex gap-1">
                        <Input value={ser.ascending || ''} onChange={(e) => updatePoint(ptIdx, serIdx, 'ascending', e.target.value)} className="h-7 w-[70px] text-xs font-mono text-center"
                          style={{ background: ser.ascending ? okBg : undefined, borderColor: ser.ascending ? okBorder : undefined }} />
                        <Input value={ser.descending || ''} onChange={(e) => updatePoint(ptIdx, serIdx, 'descending', e.target.value)} className="h-7 w-[70px] text-xs font-mono text-center"
                          style={{ background: ser.descending ? okBg : undefined, borderColor: ser.descending ? okBorder : undefined }} />
                      </div>
                    </td>
                  ))}
                  <td className="py-1 px-2 text-center font-mono text-sm font-bold" style={{ color: 'var(--ct-accent)' }}>{pt.average?.toFixed(3)}</td>
                  <td className="py-1 px-2 text-center font-mono text-sm font-bold" style={{ color: isOk === null ? 'var(--ct-text)' : (isOk ? 'var(--ct-success)' : 'var(--ct-error)') }}>{pt.error?.toFixed(3)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {measurements.points.length === 0 && (
          <div className="text-center py-12" style={{ color: 'var(--ct-text-disabled)' }}>{t('calibration-run.table-empty')}</div>
        )}
      </div>
    </div>
  );
}
